import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, Buildings, X } from '@phosphor-icons/react';
import toast, { Toaster } from 'react-hot-toast';

export default function Companies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    nombre: '',
    rut: '',
    direccion: '',
    telefono: '',
    email: ''
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const res = await api.get('/companies');
      setCompanies(res.data);
    } catch (error) {
      toast.error('Error cargando empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/companies', form);
      toast.success('Empresa creada exitosamente');
      setShowModal(false);
      setForm({ nombre: '', rut: '', direccion: '', telefono: '', email: '' });
      fetchCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error creando empresa');
    }
  };

  return (
    <div data-testid="companies-page">
      <Toaster position="top-right" />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-2">
            Gestión SaaS
          </p>
          <h1 className="text-4xl font-bold text-neutral-900 tracking-tight">
            Empresas
          </h1>
        </div>
        <button
          onClick={() => setShowModal(true)}
          data-testid="new-company-btn"
          className="flex items-center px-4 py-2.5 bg-blue-700 text-white rounded-md hover:bg-blue-800 transition-colors text-sm font-medium"
        >
          <Plus size={18} className="mr-2" weight="bold" />
          Nueva Empresa
        </button>
      </div>

      {/* Table */}
      <div className="bg-white border border-neutral-200 rounded-md overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-neutral-500">Cargando...</div>
        ) : companies.length === 0 ? (
          <div className="p-12 text-center">
            <Buildings size={48} className="mx-auto text-neutral-300 mb-3" />
            <p className="text-neutral-500">No hay empresas registradas</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="companies-table">
            <thead className="bg-neutral-50 border-b border-neutral-200">
              <tr>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Nombre</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">RUT</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Dirección</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Teléfono</th>
                <th className="text-left py-3 px-6 text-xs uppercase tracking-widest text-neutral-500 font-medium">Email</th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company) => (
                <tr key={company.id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`company-row-${company.id}`}>
                  <td className="py-4 px-6 font-medium text-neutral-900">{company.nombre}</td>
                  <td className="py-4 px-6 text-neutral-700 font-mono text-xs">{company.rut}</td>
                  <td className="py-4 px-6 text-neutral-700">{company.direccion || '—'}</td>
                  <td className="py-4 px-6 text-neutral-700">{company.telefono || '—'}</td>
                  <td className="py-4 px-6 text-neutral-700">{company.email || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="company-modal">
          <div className="bg-white rounded-md max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <h2 className="text-xl font-bold text-neutral-900 tracking-tight">Nueva Empresa</h2>
              <button 
                onClick={() => setShowModal(false)} 
                data-testid="close-modal-btn"
                className="p-1 hover:bg-neutral-100 rounded"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="company-form">
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Nombre *
                </label>
                <input
                  type="text"
                  value={form.nombre}
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                  data-testid="company-nombre-input"
                  required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  RUT *
                </label>
                <input
                  type="text"
                  value={form.rut}
                  onChange={(e) => setForm({ ...form, rut: e.target.value })}
                  data-testid="company-rut-input"
                  required
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm font-mono"
                  placeholder="76.123.456-7"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                  Dirección
                </label>
                <input
                  type="text"
                  value={form.direccion}
                  onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                  data-testid="company-direccion-input"
                  className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                    Teléfono
                  </label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                    data-testid="company-telefono-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-neutral-700 mb-2 font-medium">
                    Email
                  </label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="company-email-input"
                    className="w-full px-4 py-2.5 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-700 text-sm"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-neutral-100 text-neutral-700 rounded-md hover:bg-neutral-200 text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  data-testid="submit-company-btn"
                  className="px-4 py-2 bg-blue-700 text-white rounded-md hover:bg-blue-800 text-sm font-medium"
                >
                  Crear Empresa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
